import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Nav, Tab, Form, Button, InputGroup, Table, Badge, ListGroup, Spinner } from 'react-bootstrap';
import { LuGlobe, LuKey, LuDownload, LuSave, LuCopy, LuRefreshCw, LuPlus, LuTrash2, LuEye, LuEyeOff, LuShieldCheck, LuWebhook, LuActivity, LuDatabase } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from 'formik';
import {
    updateSettings,
    rotateDeveloperKey,
    getDeveloperKeys,
    getWebhooks,
    addWebhook,
    deleteWebhook,
    addAllowedOrigin,
    getUsageStats,
    getProfile
} from "../../../../redux/reducers/authSlice";
import { getCustomersByBusiness } from "../../../../redux/reducers/customerSlice";
import { getInvoiceList } from "../../../../redux/reducers/invoiceSlice";
import { updateBusinessSettings, updateBusinessInfo } from "../../../../redux/reducers/businessSlice";
import { toast } from "react-toastify";
import "../../../../assets/css/business.css";
import ConfirmModal from "../../../../components/ConfirmModal";
import { formatCurrency, COUNTRY_CURRENCY_MAP, COUNTRY_TIMEZONE_OPTIONS, CURRENCY_OPTIONS, LANGUAGE_OPTIONS } from "../../../../utils/currency";

const TaxPreview = ({ currency, taxType, percentage, label }) => {
    const sampleAmount = 100;
    const taxAmount = taxType === 'percentage' ? (sampleAmount * percentage) / 100 : 0;
    const total = sampleAmount + taxAmount;

    return (
        <div className="rounded-3 p-3 mt-2" style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0' }}>
            <div className="d-flex justify-content-between small mb-1">
                <span className="text-muted">Item total</span>
                <span className="fw-semibold">{formatCurrency(sampleAmount, currency)}</span>
            </div>
            {taxType === 'percentage' && percentage > 0 && (
                <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">{label || 'Tax'} ({percentage}%)</span>
                    <span className="fw-semibold text-warning">{formatCurrency(taxAmount, currency)}</span>
                </div>
            )}
            <div className="d-flex justify-content-between small pt-2 border-top">
                <span className="fw-bold text-dark">Grand Total</span>
                <span className="fw-bold" style={{ color: '#28B0C4' }}>{formatCurrency(total, currency)}</span>
            </div>
            {taxType === 'bypass' && (
                <div className="text-muted mt-1" style={{ fontSize: '11px' }}>No automatic tax applied to invoices</div>
            )}
        </div>
    );
};

const GeneralSettings = ({ businessId: businessIdProp }) => {
    const dispatch = useDispatch();
    const { userDetail, developerData, loading } = useSelector((state) => state.auth);
    const { customerList } = useSelector((state) => state.customer);
    const { invoiceList } = useSelector((state) => state.invoice);
    const { selectedBusiness } = useSelector((state) => state.business);

    // Allow usage both from BusinessInfo (prop) and Profile (Redux fallback)
    const businessId = businessIdProp || selectedBusiness?._id;

    const [showKey, setShowKey] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const [showRotateModal, setShowRotateModal] = useState(false);
    const [newOrigin, setNewOrigin] = useState("");
    const [webhookForm, setWebhookForm] = useState({
        url: "",
        events: ["invoice.created"]
    });

    useEffect(() => {
        dispatch(getProfile());
        dispatch(getDeveloperKeys());
        dispatch(getWebhooks());
        dispatch(getUsageStats());
        if (businessId) {
            dispatch(getCustomersByBusiness(businessId));
            dispatch(getInvoiceList({ businessId, limit: 1000 }));
        }
    }, [dispatch, businessId]);

    const copyToClipboard = (text, label) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        toast.info(`${label} copied!`);
    };

    const handleAddOrigin = (e) => {
        e.preventDefault();
        if (!newOrigin) return;
        dispatch(addAllowedOrigin(newOrigin));
        setNewOrigin("");
    };

    const handleAddWebhook = (e) => {
        e.preventDefault();
        if (!webhookForm.url) return;
        dispatch(addWebhook(webhookForm));
        setWebhookForm({ url: "", events: ["invoice.created"] });
    };

    const toggleEvent = (event) => {
        setWebhookForm(prev => ({
            ...prev,
            events: prev.events.includes(event)
                ? prev.events.filter(e => e !== event)
                : [...prev.events, event]
        }));
    };

    const { individualBusiness } = useSelector((state) => state.business);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            countryOfOperation: individualBusiness?.countryOfOperation || 'canada',
            currency: individualBusiness?.jssettings?.currency || 'CAD',
            timezone: individualBusiness?.jssettings?.timezone || 'America/Toronto',
            language: individualBusiness?.jssettings?.language || 'en',
            taxType: individualBusiness?.taxByBusiness?.type || 'bypass',
            taxPercentage: individualBusiness?.taxByBusiness?.percentage || 0,
            taxLabel: individualBusiness?.taxByBusiness?.label || '',
            taxAppliesTo: individualBusiness?.taxByBusiness?.appliesTo || 'all',
        },
        onSubmit: (values) => {
            // First update the countryOfOperation if it changed (requires full business update)
            if (values.countryOfOperation !== individualBusiness?.countryOfOperation) {
                dispatch(updateBusinessInfo({
                    id: businessId,
                    data: { countryOfOperation: values.countryOfOperation }
                }));
            }

            // Then update the new regional and tax settings
            dispatch(updateBusinessSettings({
                id: businessId,
                data: {
                    currency: values.currency,
                    timezone: values.timezone,
                    language: values.language,
                    taxByBusiness: {
                        type: values.taxType,
                        percentage: Number(values.taxPercentage) || 0,
                        label: values.taxLabel,
                        appliesTo: values.taxAppliesTo
                    }
                }
            }));
        },
    });

    const handleCountryChange = (e) => {
        const country = e.target.value;
        const defaults = COUNTRY_CURRENCY_MAP[country];
        formik.setFieldValue('countryOfOperation', country);
        
        if (defaults) {
            formik.setFieldValue('currency', defaults.currency);
            formik.setFieldValue('timezone', COUNTRY_TIMEZONE_OPTIONS[country]?.[0]?.value || '');
            if (!formik.values.taxLabel) {
                formik.setFieldValue('taxLabel', defaults.taxLabel);
            }
        }
    };

    // Browser-side CSV Exporters
    const downloadCSV = (content, filename) => {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportCustomers = () => {
        if (!customerList || customerList.length === 0) {
            toast.warn("No customers available to export.");
            return;
        }

        const headers = ["ID", "Name", "Email", "Phone", "Created At"];
        const rows = customerList.map(c => [
            c._id,
            c.name || "",
            c.email || "",
            c.phone || "",
            c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ""
        ]);

        const csvContent = [headers.join(","), ...rows.map(e => e.map(val => `"${val}"`).join(","))].join("\n");
        downloadCSV(csvContent, `customers_${businessId}.csv`);
        toast.success("Customer directory exported!");
    };

    const exportInvoices = () => {
        if (!invoiceList || invoiceList.length === 0) {
            toast.warn("No invoices available to export.");
            return;
        }

        const headers = ["Invoice No", "Customer Name", "Customer Email", "Subtotal", "Tax", "Total", "Status", "Issue Date", "Due Date"];
        const rows = invoiceList.map(inv => [
            inv.invoiceNumber || "",
            inv.customer?.name || "",
            inv.customer?.email || "",
            inv.subTotal || 0,
            inv.taxTotal || 0,
            inv.total || 0,
            inv.status || "",
            inv.issueDate ? new Date(inv.issueDate).toLocaleDateString() : "",
            inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : ""
        ]);

        const csvContent = [headers.join(","), ...rows.map(e => e.map(val => `"${val}"`).join(","))].join("\n");
        downloadCSV(csvContent, `invoices_${businessId}.csv`);
        toast.success("Invoice history exported!");
    };

    return (
        <div className="general-settings-container mt-4">
            <Tab.Container defaultActiveKey="regional">
                <Row className="g-4">
                    <Col lg={3} md={4}>
                        <Card className="border-0 shadow-sm rounded-4 p-2">
                            <Nav variant="pills" className="flex-column custom-side-nav">
                                <Nav.Link eventKey="regional" className="d-flex align-items-center gap-2 py-2.5 px-3 mb-1 rounded-3">
                                    <LuGlobe size={18} /> Regional Formats
                                </Nav.Link>
                                <Nav.Link eventKey="api" className="d-flex align-items-center gap-2 py-2.5 px-3 mb-1 rounded-3">
                                    <LuKey size={18} /> API Keys & Webhooks
                                </Nav.Link>
                                <Nav.Link eventKey="export" className="d-flex align-items-center gap-2 py-2.5 px-3 rounded-3">
                                    <LuDownload size={18} /> Export Data
                                </Nav.Link>
                            </Nav>
                        </Card>
                    </Col>

                    <Col lg={9} md={8}>
                        <Tab.Content>
                            {/* Regional Formats Pane */}
                            <Tab.Pane eventKey="regional">
                                <Form onSubmit={formik.handleSubmit}>
                                    <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
                                        <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                                            <LuGlobe className="text-brand" style={{ color: '#28B0C4' }} /> Regional & Currency Settings
                                        </h5>
                                        <Row className="g-4">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">Country of Operation <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select 
                                                        name="countryOfOperation" 
                                                        value={formik.values.countryOfOperation}
                                                        onChange={handleCountryChange}
                                                        className="bg-light border-0 py-2"
                                                    >
                                                        <option value="canada">Canada</option>
                                                        <option value="usa">United States</option>
                                                        <option value="mexico">Mexico</option>
                                                        <option value="nepal">Nepal</option>
                                                    </Form.Select>
                                                    <Form.Text className="text-muted" style={{fontSize: '11px'}}>
                                                        ℹ️ Changing country auto-updates currency & timezone
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">Language</Form.Label>
                                                    <Form.Select name="language" {...formik.getFieldProps('language')} className="bg-light border-0 py-2">
                                                        {LANGUAGE_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">Currency</Form.Label>
                                                    <Form.Select name="currency" {...formik.getFieldProps('currency')} className="bg-light border-0 py-2">
                                                        {COUNTRY_CURRENCY_MAP[formik.values.countryOfOperation]?.currencyOptions?.map(c => (
                                                            <option key={c} value={c}>{CURRENCY_OPTIONS.find(opt => opt.value === c)?.label || c}</option>
                                                        )) || CURRENCY_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">Timezone</Form.Label>
                                                    <Form.Select name="timezone" {...formik.getFieldProps('timezone')} className="bg-light border-0 py-2">
                                                        {(COUNTRY_TIMEZONE_OPTIONS[formik.values.countryOfOperation] || []).map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card>

                                    <Card className="border-0 shadow-sm rounded-4 p-4">
                                        <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                                            <span style={{ color: '#28B0C4' }}>%</span> Business Tax Configuration
                                        </h5>
                                        <Row className="g-4">
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold mb-3 d-block">Tax Application Rule</Form.Label>
                                                    <div className="d-flex gap-4">
                                                        <Form.Check 
                                                            type="radio"
                                                            id="tax-bypass"
                                                            label={<span className="ms-1">Bypass <small className="text-muted d-block" style={{fontSize: '11px'}}>No automatic tax applied</small></span>}
                                                            name="taxType"
                                                            value="bypass"
                                                            checked={formik.values.taxType === 'bypass'}
                                                            onChange={formik.handleChange}
                                                        />
                                                        <Form.Check 
                                                            type="radio"
                                                            id="tax-percentage"
                                                            label={<span className="ms-1">Percentage <small className="text-muted d-block" style={{fontSize: '11px'}}>Apply tax to all invoices</small></span>}
                                                            name="taxType"
                                                            value="percentage"
                                                            checked={formik.values.taxType === 'percentage'}
                                                            onChange={formik.handleChange}
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>

                                            {formik.values.taxType === 'percentage' && (
                                                <>
                                                    <Col md={4}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold">Tax Label <span className="text-danger">*</span></Form.Label>
                                                            <Form.Control 
                                                                name="taxLabel" 
                                                                {...formik.getFieldProps('taxLabel')} 
                                                                placeholder="e.g. GST, VAT, HST"
                                                                className="bg-light border-0 py-2"
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold">Tax Rate (%) <span className="text-danger">*</span></Form.Label>
                                                            <InputGroup>
                                                                <Form.Control 
                                                                    type="number" 
                                                                    min="0" max="100" step="0.01"
                                                                    name="taxPercentage" 
                                                                    {...formik.getFieldProps('taxPercentage')} 
                                                                    className="bg-light border-0 py-2"
                                                                />
                                                                <InputGroup.Text className="bg-light border-0">%</InputGroup.Text>
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Form.Group>
                                                            <Form.Label className="small fw-bold">Applies To</Form.Label>
                                                            <Form.Select name="taxAppliesTo" {...formik.getFieldProps('taxAppliesTo')} className="bg-light border-0 py-2">
                                                                <option value="all">All items</option>
                                                                <option value="products_only">Products only</option>
                                                                <option value="services_only">Services only</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                </>
                                            )}

                                            <Col md={12}>
                                                <Form.Label className="small fw-bold mt-2">Preview</Form.Label>
                                                <TaxPreview 
                                                    currency={formik.values.currency}
                                                    taxType={formik.values.taxType}
                                                    percentage={formik.values.taxPercentage}
                                                    label={formik.values.taxLabel}
                                                />
                                            </Col>

                                            <Col md={12} className="mt-4">
                                                <Button type="submit" variant="brand" className="rounded-pill px-4" disabled={loading} style={{ backgroundColor: '#28B0C4', borderColor: '#28B0C4', color: '#fff' }}>
                                                    {loading ? <Spinner as="span" animation="border" size="sm" /> : <><LuSave className="me-2" /> Save Settings</>}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Form>
                            </Tab.Pane>

                            {/* API Keys Pane */}
                            <Tab.Pane eventKey="api">
                                <div className="developer-platform-container">
                                    {/* 1. Usage Statistics */}
                                    <Row className="g-3 mb-4">
                                        <Col md={4}>
                                            <Card className="border-0 shadow-sm rounded-4 p-3 bg-white text-center h-100">
                                                <div className="d-flex align-items-center justify-content-center mb-2">
                                                    <div className="p-2 rounded-circle bg-light text-brand me-2" style={{ color: '#28B0C4' }}>
                                                        <LuActivity size={20} />
                                                    </div>
                                                    <span className="small text-muted fw-bold text-uppercase">Total Requests</span>
                                                </div>
                                                <h3 className="fw-black mb-0">{developerData.usage?.totalRequests || 0}</h3>
                                                <small className="text-muted">Last 30 days</small>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="border-0 shadow-sm rounded-4 p-3 bg-white text-center h-100">
                                                <div className="d-flex align-items-center justify-content-center mb-2">
                                                    <div className="p-2 rounded-circle bg-light text-success me-2">
                                                        <LuShieldCheck size={20} />
                                                    </div>
                                                    <span className="small text-muted fw-bold text-uppercase">Rate Limit</span>
                                                </div>
                                                <h3 className="fw-black mb-0">100/min</h3>
                                                <Badge bg="success" className="rounded-pill">Active</Badge>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="border-0 shadow-sm rounded-4 p-3 bg-white text-center h-100">
                                                <div className="d-flex align-items-center justify-content-center mb-2">
                                                    <div className="p-2 rounded-circle bg-light text-info me-2">
                                                        <LuWebhook size={20} />
                                                    </div>
                                                    <span className="small text-muted fw-bold text-uppercase">Webhooks</span>
                                                </div>
                                                <h3 className="fw-black mb-0">{developerData.webhooks?.length || 0}</h3>
                                                <small className="text-muted">Active listeners</small>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* 2. API Credentials */}
                                    <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
                                        <div className="d-flex justify-content-between align-items-start mb-4">
                                            <div>
                                                <h5 className="fw-bold mb-1">API Credentials</h5>
                                                <p className="text-muted small mb-0">Security keys for authenticating your requests.</p>
                                            </div>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="rounded-pill px-3"
                                                onClick={() => setShowRotateModal(true)}
                                            >
                                                <LuRefreshCw className="me-1" /> Rotate Key
                                            </Button>
                                        </div>

                                        <Row>
                                            <Col lg={6} className="mb-3">
                                                <Form.Label className="small fw-bold text-uppercase text-muted">Master API Key</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        readOnly
                                                        type={showKey ? "text" : "password"}
                                                        value={developerData.keys?.apiKey || '********************************'}
                                                        className="bg-light border-0 font-monospace py-2"
                                                    />
                                                    <Button variant="outline-light" className="border text-muted" onClick={() => setShowKey(!showKey)}>
                                                        {showKey ? <LuEyeOff /> : <LuEye />}
                                                    </Button>
                                                    <Button variant="outline-brand" style={{ borderColor: '#28B0C4', color: '#28B0C4' }} onClick={() => copyToClipboard(developerData.keys?.apiKey, "API Key")}>
                                                        <LuCopy />
                                                    </Button>
                                                </InputGroup>
                                            </Col>
                                            <Col lg={6} className="mb-3">
                                                <Form.Label className="small fw-bold text-uppercase text-muted">Webhook Signing Secret</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        readOnly
                                                        type={showSecret ? "text" : "password"}
                                                        value={developerData.keys?.webhookSecret || '********************************'}
                                                        className="bg-light border-0 font-monospace py-2"
                                                    />
                                                    <Button variant="outline-light" className="border text-muted" onClick={() => setShowSecret(!showSecret)}>
                                                        {showSecret ? <LuEyeOff /> : <LuEye />}
                                                    </Button>
                                                    <Button variant="outline-brand" style={{ borderColor: '#28B0C4', color: '#28B0C4' }} onClick={() => copyToClipboard(developerData.keys?.webhookSecret, "Webhook Secret")}>
                                                        <LuCopy />
                                                    </Button>
                                                </InputGroup>
                                            </Col>
                                        </Row>
                                    </Card>

                                    {/* 3. Allowed Origins & Webhook Subscriptions */}
                                    <Row className="g-4">
                                        <Col lg={6}>
                                            <Card className="border-0 shadow-sm rounded-4 p-4 h-100">
                                                <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                                    <LuGlobe size={20} className="text-brand" style={{ color: '#28B0C4' }} /> Allowed Origins (CORS)
                                                </h5>
                                                <p className="text-muted small mb-4">Domains allowed to call the API from the browser.</p>

                                                <Form onSubmit={handleAddOrigin} className="mb-4">
                                                    <InputGroup>
                                                        <Form.Control
                                                            placeholder="https://yourwebsite.com"
                                                            value={newOrigin}
                                                            onChange={(e) => setNewOrigin(e.target.value)}
                                                            className="border-0 bg-light py-2"
                                                        />
                                                        <Button variant="brand" type="submit" disabled={!newOrigin} style={{ backgroundColor: '#28B0C4', borderColor: '#28B0C4', color: '#fff' }}>
                                                            <LuPlus /> Add
                                                        </Button>
                                                    </InputGroup>
                                                </Form>

                                                <ListGroup variant="flush">
                                                    {userDetail?.allowedOrigins?.map((origin, idx) => (
                                                        <ListGroup.Item key={idx} className="px-0 py-2 d-flex justify-content-between align-items-center border-0 border-bottom">
                                                            <span className="small text-muted font-monospace">{origin}</span>
                                                        </ListGroup.Item>
                                                    ))}
                                                    {(!userDetail?.allowedOrigins || userDetail.allowedOrigins.length === 0) && (
                                                        <div className="text-center py-4 bg-light rounded-3 text-muted small">
                                                            No origins registered. API calls will be restricted to first-party apps.
                                                        </div>
                                                    )}
                                                </ListGroup>
                                            </Card>
                                        </Col>

                                        <Col lg={6}>
                                            <Card className="border-0 shadow-sm rounded-4 p-4 h-100">
                                                <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                                    <LuWebhook size={20} className="text-brand" style={{ color: '#28B0C4' }} /> Webhook Subscriptions
                                                </h5>
                                                <p className="text-muted small mb-4">Receive real-time notifications for specific events.</p>

                                                <Form onSubmit={handleAddWebhook} className="mb-4">
                                                    <Form.Control
                                                        placeholder="https://yourserver.com/api/webhook"
                                                        value={webhookForm.url}
                                                        onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                                                        className="border-0 bg-light py-2 mb-2"
                                                    />
                                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                                        {['invoice.created', 'invoice.sent', 'invoice.paid'].map(event => (
                                                            <Badge
                                                                key={event}
                                                                bg={webhookForm.events.includes(event) ? "brand" : "light"}
                                                                className={`cursor-pointer border ${webhookForm.events.includes(event) ? "text-white bg-brand" : "text-muted"}`}
                                                                style={webhookForm.events.includes(event) ? { backgroundColor: '#28B0C4', borderColor: '#28B0C4' } : {}}
                                                                onClick={() => toggleEvent(event)}
                                                            >
                                                                {event}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                    <Button variant="brand" type="submit" className="w-100 rounded-pill" disabled={!webhookForm.url} style={{ backgroundColor: '#28B0C4', borderColor: '#28B0C4', color: '#fff' }}>
                                                        <LuPlus className="me-1" /> Register Webhook
                                                    </Button>
                                                </Form>

                                                <Table responsive className="small">
                                                    <tbody>
                                                        {developerData.webhooks?.map((webhook) => (
                                                            <tr key={webhook._id} className="align-middle border-bottom">
                                                                <td className="ps-0 py-3">
                                                                    <div className="font-monospace text-truncate" style={{ maxWidth: '150px' }} title={webhook.url}>{webhook.url}</div>
                                                                    <div className="mt-1 d-flex gap-1 overflow-auto">
                                                                        {webhook.events?.map(e => <Badge key={e} bg="soft-brand text-brand" className="x-small">{e}</Badge>)}
                                                                    </div>
                                                                </td>
                                                                <td className="text-end pe-0">
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        className="border-0 p-1"
                                                                        onClick={() => dispatch(deleteWebhook(webhook._id))}
                                                                    >
                                                                        <LuTrash2 size={16} />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </Card>
                                        </Col>
                                    </Row>
                                </div>
                            </Tab.Pane>

                            {/* Export Data Pane */}
                            <Tab.Pane eventKey="export">
                                <Card className="border-0 shadow-sm rounded-4 p-4">
                                    <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                                        <LuDownload className="text-brand" style={{ color: '#28B0C4' }} /> Export Data
                                    </h5>
                                    <p className="text-muted small mb-4">Safely export and manage your business records in CSV format.</p>
                                    <div className="export-options">
                                        <div className="d-flex align-items-center justify-content-between p-3 border rounded-3 mb-3 hover-effect">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="p-2 bg-success bg-opacity-10 text-success rounded">
                                                    <LuDatabase size={20} />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-bold">Customer Directory</h6>
                                                    <small className="text-muted">Download all customer profiles in CSV format</small>
                                                </div>
                                            </div>
                                            <Button variant="light" size="sm" onClick={exportCustomers}><LuDownload /></Button>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between p-3 border rounded-3 hover-effect">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="p-2 bg-info bg-opacity-10 text-info rounded">
                                                    <LuDownload size={20} />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-bold">Invoice History</h6>
                                                    <small className="text-muted">Full list of all business invoices and records</small>
                                                </div>
                                            </div>
                                            <Button variant="light" size="sm" onClick={exportInvoices}><LuDownload /></Button>
                                        </div>
                                    </div>
                                </Card>
                            </Tab.Pane>
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>
            <ConfirmModal
                show={showRotateModal}
                onHide={() => setShowRotateModal(false)}
                onConfirm={() => {
                    dispatch(rotateDeveloperKey());
                    setShowRotateModal(false);
                }}
                title="Rotate API Key"
                message="Changing your API key will immediately break any existing external integrations or third-party apps using it. Are you sure you want to continue?"
                confirmText="Yes, Rotate"
                variant="danger"
                icon={LuRefreshCw}
            />
        </div>
    );
};

export default GeneralSettings;
